import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../config/api';
import SystemDbBrowserView from './SystemDbBrowserView';

const RUN_PROGRESS_CONFIG = [
  { taskName: 'kepco_address_coordinates', title: '한전 주소 재수집' },
  { taskName: 'vworld_address_coordinates', title: 'VWORLD 좌표 재수집' },
  { taskName: 'naver_address_coordinates', title: '네이버 클라우드 지도 API 재수집' },
  { taskName: 'kakao_address_coordinates', title: '카카오 지도 API 재수집' },
  { taskName: 'kepco_address_line_capacity', title: '한전 선로용량 수집' },
  { taskName: 'land_use_address_info', title: '토지이용정보 수집' },
];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function SystemDbUpdateView() {
  const [sidoOptions, setSidoOptions] = useState([]);
  const [selectedSido, setSelectedSido] = useState('');
  const [runningKepcoCoord, setRunningKepcoCoord] = useState(false);
  const [runningVworldCoord, setRunningVworldCoord] = useState(false);
  const [runningNaverCoord, setRunningNaverCoord] = useState(false);
  const [runningKakaoCoord, setRunningKakaoCoord] = useState(false);
  const [runningKepcoLine, setRunningKepcoLine] = useState(false);
  const [runningLandInfo, setRunningLandInfo] = useState(false);
  const [creatingKepcoAwsPlan, setCreatingKepcoAwsPlan] = useState(false);
  const [launchingKepcoAwsEc2, setLaunchingKepcoAwsEc2] = useState(false);
  const [kepcoAwsLaunchMode, setKepcoAwsLaunchMode] = useState('');
  const [kepcoAwsPlanFile, setKepcoAwsPlanFile] = useState('');
  const [kepcoAwsDryRun, setKepcoAwsDryRun] = useState(true);
  const [kepcoAwsJobs, setKepcoAwsJobs] = useState([]);
  const [selectedKepcoAwsInstanceId, setSelectedKepcoAwsInstanceId] = useState('');
  const [selectedKepcoAwsInstanceMeta, setSelectedKepcoAwsInstanceMeta] = useState(null);
  const [selectedKepcoAwsInstanceLogLines, setSelectedKepcoAwsInstanceLogLines] = useState([]);
  const [loadingKepcoAwsInstanceLogs, setLoadingKepcoAwsInstanceLogs] = useState(false);
  const [kepcoLineUpdateMode, setKepcoLineUpdateMode] = useState('capacity_only');
  const [kepcoLineOutputMode, setKepcoLineOutputMode] = useState('db');
  const [coordCollectionMode, setCoordCollectionMode] = useState('kepco_address');
  const [kepcoCoordRunMeta, setKepcoCoordRunMeta] = useState(null);
  const [vworldCoordRunMeta, setVworldCoordRunMeta] = useState(null);
  const [naverCoordRunMeta, setNaverCoordRunMeta] = useState(null);
  const [kakaoCoordRunMeta, setKakaoCoordRunMeta] = useState(null);
  const [kepcoLineRunMeta, setKepcoLineRunMeta] = useState(null);
  const [landInfoRunMeta, setLandInfoRunMeta] = useState(null);
  const [runProgressByTask, setRunProgressByTask] = useState({});
  const [linePreview, setLinePreview] = useState(null);
  const [landInfoPreview, setLandInfoPreview] = useState(null);
  const [vworldTargetPreview, setVworldTargetPreview] = useState(null);
  const [checkingVworldTargets, setCheckingVworldTargets] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const getToken = () => localStorage.getItem('authToken');
  const isAuthErrorMessage = (message) =>
    ['로그인이 필요합니다.', 'Missing token.', 'Token expired.', 'Invalid token.'].includes(message);

  const resetLocalRunStates = () => {
    setRunningKepcoCoord(false);
    setRunningVworldCoord(false);
    setRunningNaverCoord(false);
    setRunningKakaoCoord(false);
    setRunningKepcoLine(false);
    setRunningLandInfo(false);
  };

  const handleAuthFailure = (message) => {
    if (!isAuthErrorMessage(message)) {
      return false;
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('is2FAVerified');
    resetLocalRunStates();
    setErrorMessage('세션이 만료되었습니다. 다시 로그인해주세요.');
    window.location.replace('/login');
    return true;
  };

  const readApiJson = async (response, requestUrl) => {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const responseText = await response.text();
      const preview = responseText.replace(/\s+/g, ' ').slice(0, 120);
      throw new Error(`API JSON 응답이 아닙니다: ${requestUrl} / HTTP ${response.status} / ${preview}`);
    }
    return response.json();
  };

  const apiGet = async (path) => {
    const token = getToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    const requestUrl = `${API_BASE_URL}${path}`;
    let response;
    try {
      response = await fetch(requestUrl, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
    } catch (error) {
      throw new Error(`API 연결 실패: ${requestUrl}`);
    }

    const data = await readApiJson(response, requestUrl);

    if (!(response.ok && data.status === 'success')) {
      throw new Error(data.message || '요청에 실패했습니다.');
    }

    return data;
  };

  const apiPost = async (path, body) => {
    const token = getToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    const requestUrl = `${API_BASE_URL}${path}`;
    let response;
    try {
      response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(body || {}),
      });
    } catch (error) {
      throw new Error(`API 연결 실패: ${requestUrl}`);
    }

    const data = await readApiJson(response, requestUrl);

    if (!(response.ok && data.status === 'success')) {
      throw new Error(data.message || '요청에 실패했습니다.');
    }

    return data;
  };

  const fetchRunStatus = async () => {
    try {
      const data = await apiGet('/api/system-db/run-status');
      const kepcoCoordRun = data.runs?.kepco_address_coordinates || {};
      const vworldCoordRun = data.runs?.vworld_address_coordinates || {};
      const naverCoordRun = data.runs?.naver_address_coordinates || {};
      const kakaoCoordRun = data.runs?.kakao_address_coordinates || {};
      const kepcoLineRun = data.runs?.kepco_address_line_capacity || {};
      const landInfoRun = data.runs?.land_use_address_info || {};

      setRunningKepcoCoord(Boolean(kepcoCoordRun.is_running));
      setRunningVworldCoord(Boolean(vworldCoordRun.is_running));
      setRunningNaverCoord(Boolean(naverCoordRun.is_running));
      setRunningKakaoCoord(Boolean(kakaoCoordRun.is_running));
      setRunningKepcoLine(Boolean(kepcoLineRun.is_running));
      setRunningLandInfo(Boolean(landInfoRun.is_running));
      setKepcoCoordRunMeta(kepcoCoordRun);
      setVworldCoordRunMeta(vworldCoordRun);
      setNaverCoordRunMeta(naverCoordRun);
      setKakaoCoordRunMeta(kakaoCoordRun);
      setKepcoLineRunMeta(kepcoLineRun);
      setLandInfoRunMeta(landInfoRun);
      setRunProgressByTask(data.progress || {});
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
    }
  };

  const fetchSidoOptions = async () => {
    try {
      const data = await apiGet('/api/system-db/kepco/sidos');
      const nextOptions = data.sidos || [];
      setSidoOptions(nextOptions);
      setSelectedSido((prev) => prev || nextOptions[0] || '');
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '시도 목록을 불러오지 못했습니다.');
    }
  };

  const fetchLinePreview = async (sido) => {
    if (!sido) {
      setLinePreview(null);
      return;
    }

    try {
      const data = await apiGet(`/api/system-db/address-line-capacity/preview?sido=${encodeURIComponent(sido)}`);
      setLinePreview(data);
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setLinePreview(null);
    }
  };

  const fetchLandInfoPreview = async (sido) => {
    if (!sido) {
      setLandInfoPreview(null);
      return;
    }

    try {
      const data = await apiGet(`/api/system-db/address-land-info/preview?sido=${encodeURIComponent(sido)}`);
      setLandInfoPreview(data);
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setLandInfoPreview(null);
    }
  };

  useEffect(() => {
    fetchSidoOptions();
    fetchRunStatus();
  }, []);

  useEffect(() => {
    fetchLinePreview(selectedSido);
    fetchLandInfoPreview(selectedSido);
    setVworldTargetPreview(null);
  }, [selectedSido]);

  useEffect(() => {
    setVworldTargetPreview(null);
  }, [coordCollectionMode]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchRunStatus();
    }, 3000);

    return () => window.clearInterval(timer);
  }, []);

  const collectCoordFromKepco = async () => {
    try {
      setRunningKepcoCoord(true);
      setErrorMessage('');
      setResultMessage('');

      const data = await apiPost('/api/system-db/address-coordinates/collect', {
        sido: selectedSido,
        replace: true,
      });

      setResultMessage(
        `한전 주소 재수집 완료: ${data.sido} / 저장 ${Number(data.stored_row_count || 0).toLocaleString()}건 / 신규 ${Number(data.inserted_count || 0).toLocaleString()}건 / 업데이트 ${Number(data.updated_count || 0).toLocaleString()}건 / 제외 ${Number(data.skipped_count || 0).toLocaleString()}건`
      );

      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningKepcoCoord(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '한전 주소 재수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const formatGeocodeTargetPreviewMessage = (preview, providerLabel) => {
    const counts = preview?.counts || {};
    return [
      `${providerLabel} 재수집 대상 조회: ${preview?.sido || selectedSido}`,
      `전체 데이터 ${Number(counts.total_rows || 0).toLocaleString()}건`,
      `재수집 대상 ${Number(counts.target_rows || 0).toLocaleString()}건`,
      `성공 제외 ${Number(counts.success_rows || 0).toLocaleString()}건`,
      `NO_COORD ${Number(counts.no_coord_rows || 0).toLocaleString()}건`,
      `NO_COORD_IN_VWORLD ${Number(counts.no_coord_in_vworld_rows || 0).toLocaleString()}건`,
      `NO_COORD_IN_NAVER ${Number(counts.no_coord_in_naver_rows || 0).toLocaleString()}건`,
      `NO_COORD_IN_VWORLD_AND_NAVER ${Number(counts.no_coord_in_vworld_and_naver_rows || 0).toLocaleString()}건`,
      `NO_COORD_IN_KAKAO ${Number(counts.no_coord_in_kakao_rows || 0).toLocaleString()}건`,
      `NO_COORD_IN_ALL_API ${Number(counts.no_coord_in_all_api_rows || 0).toLocaleString()}건`,
      `ERROR ${Number(counts.error_rows || 0).toLocaleString()}건`,
      `PENDING ${Number(counts.pending_rows || 0).toLocaleString()}건`,
      `좌표누락 ${Number(counts.missing_coord_rows || 0).toLocaleString()}건`,
      `기타상태 ${Number(counts.other_status_rows || 0).toLocaleString()}건`,
      `샘플 ${Number(preview?.samples?.length || 0).toLocaleString()}건`,
      Number(counts.target_rows || 0) > 0 ? `다시 누르면 실제 ${providerLabel} 재수집을 시작합니다.` : '재수집 대상이 없어 실행하지 않습니다.',
    ].join(' / ');
  };

  const fetchGeocodeTargetPreview = async (provider, providerLabel) => {
    setCheckingVworldTargets(true);
    setErrorMessage('');
    setResultMessage('');
    try {
      const data = await apiGet(`/api/system-db/address-coordinates/geocode-targets?sido=${encodeURIComponent(selectedSido)}&provider=${encodeURIComponent(provider)}&limit=10`);
      setVworldTargetPreview(data);
      setResultMessage(formatGeocodeTargetPreviewMessage(data, providerLabel));
      return data;
    } finally {
      setCheckingVworldTargets(false);
    }
  };

  const formatGeocodeStatusSummary = (statusCounts) => {
    const totalRows = Number(statusCounts.total_rows || 0);
    return Object.keys(statusCounts).length
      ? ` / 전체 데이터 ${totalRows.toLocaleString()}건 / 성공제외 ${Number(statusCounts.success_rows || 0).toLocaleString()}건 / NO_COORD ${Number(statusCounts.no_coord_rows || 0).toLocaleString()}건 / NO_COORD_IN_VWORLD ${Number(statusCounts.no_coord_in_vworld_rows || 0).toLocaleString()}건 / NO_COORD_IN_NAVER ${Number(statusCounts.no_coord_in_naver_rows || 0).toLocaleString()}건 / NO_COORD_IN_VWORLD_AND_NAVER ${Number(statusCounts.no_coord_in_vworld_and_naver_rows || 0).toLocaleString()}건 / NO_COORD_IN_KAKAO ${Number(statusCounts.no_coord_in_kakao_rows || 0).toLocaleString()}건 / NO_COORD_IN_ALL_API ${Number(statusCounts.no_coord_in_all_api_rows || 0).toLocaleString()}건 / ERROR ${Number(statusCounts.error_rows || 0).toLocaleString()}건 / PENDING ${Number(statusCounts.pending_rows || 0).toLocaleString()}건 / 좌표누락 ${Number(statusCounts.missing_coord_rows || 0).toLocaleString()}건 / 기타상태 ${Number(statusCounts.other_status_rows || 0).toLocaleString()}건`
      : '';
  };

  const collectCoordFromVworld = async () => {
    try {
      setErrorMessage('');

      if (!vworldTargetPreview || vworldTargetPreview.sido !== selectedSido) {
        const preview = await fetchGeocodeTargetPreview('vworld', 'VWORLD 좌표');
        if (Number(preview?.counts?.target_rows || 0) <= 0) {
          return;
        }
        return;
      }

      if (Number(vworldTargetPreview?.counts?.target_rows || 0) <= 0) {
        setResultMessage(formatGeocodeTargetPreviewMessage(vworldTargetPreview, 'VWORLD 좌표'));
        return;
      }

      setRunningVworldCoord(true);

      const data = await apiPost('/api/system-db/address-coordinates/geocode', {
        sido: selectedSido,
      });

      const statusCounts = data.status_counts || {};
      const targetRows = Number(data.target_count || statusCounts.target_rows || 0);
      const statusSummary = formatGeocodeStatusSummary(statusCounts);

      setResultMessage(
        `VWORLD 좌표 재수집 완료: ${data.sido} / 재수집 대상 ${targetRows.toLocaleString()}건 / 좌표 반영 ${Number(data.ok_count || 0).toLocaleString()}건 / 좌표 없음 ${Number(data.no_coord_count || 0).toLocaleString()}건 / 오류 ${Number(data.error_count || 0).toLocaleString()}건${statusSummary}`
      );

      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningVworldCoord(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || 'VWORLD 좌표 재수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const collectCoordFromNaver = async () => {
    try {
      setErrorMessage('');

      if (!vworldTargetPreview || vworldTargetPreview.sido !== selectedSido) {
        const preview = await fetchGeocodeTargetPreview('naver', '네이버 클라우드 지도 API');
        if (Number(preview?.counts?.target_rows || 0) <= 0) {
          return;
        }
        return;
      }

      if (Number(vworldTargetPreview?.counts?.target_rows || 0) <= 0) {
        setResultMessage(formatGeocodeTargetPreviewMessage(vworldTargetPreview, '네이버 클라우드 지도 API'));
        return;
      }

      setRunningNaverCoord(true);

      const data = await apiPost('/api/system-db/address-coordinates/geocode-naver', {
        sido: selectedSido,
      });

      const statusCounts = data.status_counts || {};
      const targetRows = Number(data.target_count || statusCounts.target_rows || 0);
      const statusSummary = formatGeocodeStatusSummary(statusCounts);

      setResultMessage(
        `네이버 클라우드 지도 API 재수집 완료: ${data.sido} / 재수집 대상 ${targetRows.toLocaleString()}건 / 좌표 반영 ${Number(data.ok_count || 0).toLocaleString()}건 / 좌표 없음 ${Number(data.no_coord_count || 0).toLocaleString()}건 / 오류 ${Number(data.error_count || 0).toLocaleString()}건${statusSummary}`
      );

      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningNaverCoord(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '네이버 클라우드 지도 API 재수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const collectCoordFromKakao = async () => {
    try {
      setErrorMessage('');

      if (!vworldTargetPreview || vworldTargetPreview.sido !== selectedSido) {
        const preview = await fetchGeocodeTargetPreview('kakao', '카카오 지도 API');
        if (Number(preview?.counts?.target_rows || 0) <= 0) {
          return;
        }
        return;
      }

      if (Number(vworldTargetPreview?.counts?.target_rows || 0) <= 0) {
        setResultMessage(formatGeocodeTargetPreviewMessage(vworldTargetPreview, '카카오 지도 API'));
        return;
      }

      setRunningKakaoCoord(true);

      const data = await apiPost('/api/system-db/address-coordinates/geocode-kakao', {
        sido: selectedSido,
      });

      const statusCounts = data.status_counts || {};
      const targetRows = Number(data.target_count || statusCounts.target_rows || 0);
      const statusSummary = formatGeocodeStatusSummary(statusCounts);

      setResultMessage(
        `카카오 지도 API 재수집 완료: ${data.sido} / 재수집 대상 ${targetRows.toLocaleString()}건 / 좌표 반영 ${Number(data.ok_count || 0).toLocaleString()}건 / 좌표 없음 ${Number(data.no_coord_count || 0).toLocaleString()}건 / 오류 ${Number(data.error_count || 0).toLocaleString()}건${statusSummary}`
      );

      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningKakaoCoord(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '카카오 지도 API 재수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const collectLineFromKepco = async () => {
    try {
      setRunningKepcoLine(true);
      setErrorMessage('');
      setResultMessage('');

      const data = await apiPost('/api/system-db/address-line-capacity/collect', {
        sido: selectedSido,
        replace: kepcoLineUpdateMode === 'full',
        address_source: 'db',
        line_update_mode: kepcoLineUpdateMode,
        output_mode: kepcoLineOutputMode,
      });

      const exportedFiles = data.exported_files || [];
      const exportSuffix = data.output_mode === 'csv'
        ? ` / CSV 파일 ${Number(exportedFiles.length || 0).toLocaleString()}개${exportedFiles[0] ? ` / 위치 ${exportedFiles[0]}` : ''}`
        : '';
      setResultMessage(
        `한전 선로용량 수집 완료: ${data.sido} / 저장 ${Number(data.stored_row_count || 0).toLocaleString()}건 / 신규 ${Number(data.inserted_count || 0).toLocaleString()}건 / 업데이트 ${Number(data.updated_count || 0).toLocaleString()}건 / 제외 ${Number(data.skipped_count || 0).toLocaleString()}건${exportSuffix}`
      );

      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningKepcoLine(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '한전 선로용량 수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const createKepcoAwsPlan = async () => {
    try {
      setCreatingKepcoAwsPlan(true);
      setErrorMessage('');
      setResultMessage('');

      const data = await apiPost('/api/system-db/address-line-capacity/aws-plan', {
        sido: selectedSido,
        address_source: 'db',
        line_update_mode: kepcoLineUpdateMode,
        output_mode: kepcoLineOutputMode,
      });

      setKepcoAwsPlanFile(data.plan_file || '');

      setResultMessage(
        `AWS 한전 선로용량 도별 작업 계획 생성 완료: ${data.sido} / 생성할 PC ${Number(data.pc_count || 0).toLocaleString()}대 / 대상 ${Number(data.target_sidos?.length || 0).toLocaleString()}개 시도 / 계획 파일 ${data.plan_file}`
      );
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || 'AWS 작업 계획 생성 중 오류가 발생했습니다.');
    } finally {
      setCreatingKepcoAwsPlan(false);
    }
  };

  const launchKepcoAwsEc2 = async (dryRun) => {
    try {
      setLaunchingKepcoAwsEc2(true);
      setKepcoAwsLaunchMode(dryRun ? 'dry_run' : 'launch');
      setErrorMessage('');
      setResultMessage('');

      const data = await apiPost('/api/system-db/address-line-capacity/aws-launch', {
        plan_file: kepcoAwsPlanFile,
        dry_run: dryRun,
      });

      const sampleJob = (data.jobs || [])[0];
      setKepcoAwsJobs(data.jobs || []);
      setSelectedKepcoAwsInstanceId(sampleJob?.log_instance_id || sampleJob?.instance_id || '');
      setSelectedKepcoAwsInstanceMeta(null);
      setSelectedKepcoAwsInstanceLogLines([]);
      const sampleSuffix = sampleJob
        ? dryRun
          ? ` / 예시 ${sampleJob.instance_name} (${sampleJob.sido})`
          : ` / 첫 인스턴스 ${sampleJob.instance_id || '-'} (${sampleJob.instance_name})`
        : '';

      setResultMessage(
        dryRun
          ? `AWS EC2 설정 점검 완료: 계획 파일 ${data.plan_file} / 점검 대상 ${Number(data.jobs?.length || 0).toLocaleString()}대 / 부트스트랩 패키지 ${data.bootstrap_package || '-'}${sampleSuffix}`
          : `AWS EC2 생성 완료: 계획 파일 ${data.plan_file} / 생성 요청 ${Number(data.jobs?.length || 0).toLocaleString()}대 / 부트스트랩 패키지 ${data.bootstrap_package || '-'}${sampleSuffix}`
      );
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || (dryRun ? 'AWS EC2 설정 점검 중 오류가 발생했습니다.' : 'AWS EC2 생성 중 오류가 발생했습니다.'));
    } finally {
      setLaunchingKepcoAwsEc2(false);
      setKepcoAwsLaunchMode('');
    }
  };

  const fetchKepcoAwsInstanceLogs = async (instanceId) => {
    if (!instanceId) {
      setSelectedKepcoAwsInstanceId('');
      setSelectedKepcoAwsInstanceMeta(null);
      setSelectedKepcoAwsInstanceLogLines([]);
      return;
    }

    try {
      setLoadingKepcoAwsInstanceLogs(true);
      setSelectedKepcoAwsInstanceId(instanceId);
      const data = await apiGet(`/api/system-db/address-line-capacity/aws-worker-log?instance_id=${encodeURIComponent(instanceId)}&limit=200`);
      setSelectedKepcoAwsInstanceMeta(data.meta || null);
      setSelectedKepcoAwsInstanceLogLines(data.entries || []);
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || 'AWS 인스턴스 로그를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoadingKepcoAwsInstanceLogs(false);
    }
  };

  const collectLandInfo = async () => {
    try {
      setRunningLandInfo(true);
      setErrorMessage('');
      setResultMessage('');

      const data = await apiPost('/api/system-db/address-land-info/collect', {
        sido: selectedSido,
      });

      setResultMessage(
        `토지이용정보 수집 완료: ${data.sido} / 대상 ${Number(data.target_count || 0).toLocaleString()}건 / 저장 ${Number(data.stored_row_count || 0).toLocaleString()}건 / 신규 ${Number(data.inserted_count || 0).toLocaleString()}건 / 업데이트 ${Number(data.updated_count || 0).toLocaleString()}건 / 용도지역·지목 반영 ${Number(data.ok_count || 0).toLocaleString()}건 / 정보 없음 ${Number(data.empty_count || 0).toLocaleString()}건 / 오류 ${Number(data.error_count || 0).toLocaleString()}건`
      );
      fetchLandInfoPreview(selectedSido);
      fetchRunStatus();
    } catch (error) {
      console.error(error);
      setRunningLandInfo(false);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || '토지이용정보 수집 중 오류가 발생했습니다.');
      fetchRunStatus();
    } finally {
      fetchRunStatus();
    }
  };

  const runSelectedCoordCollection = () => {
    if (coordCollectionMode === 'vworld_geocode') {
      collectCoordFromVworld();
      return;
    }
    if (coordCollectionMode === 'naver_geocode') {
      collectCoordFromNaver();
      return;
    }
    if (coordCollectionMode === 'kakao_geocode') {
      collectCoordFromKakao();
      return;
    }
    collectCoordFromKepco();
  };

  const cancelTask = async (taskName, label) => {
    try {
      setErrorMessage('');
      setResultMessage('');
      const data = await apiPost('/api/system-db/run-cancel', { task_name: taskName });
      setResultMessage(data.message || `${label} 중단 요청이 접수되었습니다.`);
      fetchRunStatus();
    } catch (error) {
      console.error(error);
      if (handleAuthFailure(error.message)) return;
      setErrorMessage(error.message || `${label} 중단 요청 중 오류가 발생했습니다.`);
    }
  };

  const activeCoordRunMeta = runningKakaoCoord
    ? kakaoCoordRunMeta
    : runningNaverCoord
    ? naverCoordRunMeta
    : runningVworldCoord
      ? vworldCoordRunMeta
      : kepcoCoordRunMeta;
  const activeCoordTaskName = runningKakaoCoord
    ? 'kakao_address_coordinates'
    : runningNaverCoord
    ? 'naver_address_coordinates'
    : runningVworldCoord
      ? 'vworld_address_coordinates'
      : 'kepco_address_coordinates';
  const activeCoordTaskLabel = runningKakaoCoord
    ? '카카오 지도 API 재수집'
    : runningNaverCoord
    ? '네이버 클라우드 지도 API 재수집'
    : runningVworldCoord
      ? 'VWORLD 좌표 재수집'
      : '한전 주소 재수집';
  const isCoordCollectionRunning = runningKepcoCoord || runningVworldCoord || runningNaverCoord || runningKakaoCoord;
  const coordCollectionButtonLabel = runningKepcoCoord
    ? '한전 주소 재수집 실행 중...'
    : runningVworldCoord
      ? 'VWORLD 좌표 재수집 실행 중...'
      : runningNaverCoord
        ? '네이버 클라우드 지도 API 재수집 실행 중...'
      : runningKakaoCoord
        ? '카카오 지도 API 재수집 실행 중...'
      : checkingVworldTargets
        ? '좌표 대상 조회 중...'
      : coordCollectionMode === 'vworld_geocode'
        ? vworldTargetPreview?.sido === selectedSido && Number(vworldTargetPreview?.counts?.target_rows || 0) > 0
          ? 'VWORLD 좌표 재수집 시작'
          : 'VWORLD 좌표 재수집 대상 조회'
        : coordCollectionMode === 'naver_geocode'
          ? vworldTargetPreview?.sido === selectedSido && Number(vworldTargetPreview?.counts?.target_rows || 0) > 0
            ? '네이버 클라우드 지도 API 재수집 시작'
            : '네이버 클라우드 지도 API 재수집 대상 조회'
        : coordCollectionMode === 'kakao_geocode'
          ? vworldTargetPreview?.sido === selectedSido && Number(vworldTargetPreview?.counts?.target_rows || 0) > 0
            ? '카카오 지도 API 재수집 시작'
            : '카카오 지도 API 재수집 대상 조회'
        : '한전 주소 재수집 실행';

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      <div className="dashboard-panel dashboard-panel-wide">
        <div className="dashboard-panel-header">
          <h3>한전 직접 수집</h3>
        </div>

        <p className="dashboard-section-description" style={{ marginBottom: '16px' }}>
          시도 단위로 한전 주소 데이터를 다시 수집하고, 같은 기준으로 주소-선로용량까지 직접 받아 DB를 갱신합니다.
          요청 수는 보수적으로 제한되어 있으며, 실패 시에도 점진적으로 대기 후 다시 시도합니다.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 360px) 1fr', gap: '16px', alignItems: 'end' }}>
          <div style={{ display: 'grid', gap: '8px' }}>
            <label htmlFor="kepco-sido-select" style={{ fontWeight: 700 }}>
              시도 선택
            </label>
            <select
              id="kepco-sido-select"
              value={selectedSido}
              onChange={(e) => setSelectedSido(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                background: '#fff',
                color: '#0f172a',
              }}
            >
              {sidoOptions.map((sido) => (
                <option key={sido} value={sido}>
                  {sido}
                </option>
              ))}
            </select>
          </div>

          <div style={{ color: '#94a3b8', fontSize: '14px' }}>
            전체를 선택하면 등록된 모든 시도에 대해 순차 수집합니다. 병렬 요청 수는 낮게 유지되며, 대량 조회 시 시간이 더 오래 걸릴 수 있습니다.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', marginTop: '16px' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px' }}>
            <h4 style={{ marginTop: 0 }}>1. 한전 주소-좌표 수집</h4>
            {isCoordCollectionRunning && (
              <div style={{ marginBottom: '12px', color: '#93c5fd', fontSize: '14px', fontWeight: 700 }}>
                진행 중
                {activeCoordRunMeta?.run_label ? ` · ${activeCoordRunMeta.run_label}` : ''}
                {activeCoordRunMeta?.started_by ? ` · ${activeCoordRunMeta.started_by}` : ''}
                {activeCoordRunMeta?.started_at ? ` · ${formatDateTime(activeCoordRunMeta.started_at)}` : ''}
              </div>
            )}
            <p style={{ marginTop: 0, marginBottom: '14px', color: '#6b7280', fontSize: '14px' }}>
              같은 시도 기준으로 한전 주소를 다시 가져오거나, 이미 저장된 주소 중 좌표 보강이 필요한 행을 VWORLD, 네이버 클라우드 지도 API, 카카오 지도 API로 다시 조회할 수 있습니다.
            </p>
            <div style={{ marginBottom: '14px', fontSize: '14px', color: '#334155' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>
                수집 옵션
              </label>
              <label style={{ display: 'block', marginBottom: '6px' }}>
                <input
                  type="radio"
                  name="coord-collection-mode"
                  value="kepco_address"
                  checked={coordCollectionMode === 'kepco_address'}
                  onChange={() => setCoordCollectionMode('kepco_address')}
                  disabled={isCoordCollectionRunning}
                />{' '}
                한전 주소 재수집
              </label>
              <label style={{ display: 'block', marginBottom: '6px' }}>
                <input
                  type="radio"
                  name="coord-collection-mode"
                  value="vworld_geocode"
                  checked={coordCollectionMode === 'vworld_geocode'}
                  onChange={() => setCoordCollectionMode('vworld_geocode')}
                  disabled={isCoordCollectionRunning}
                />{' '}
                VWORLD 좌표 재수집
              </label>
              <label style={{ display: 'block', marginBottom: '6px' }}>
                <input
                  type="radio"
                  name="coord-collection-mode"
                  value="naver_geocode"
                  checked={coordCollectionMode === 'naver_geocode'}
                  onChange={() => setCoordCollectionMode('naver_geocode')}
                  disabled={isCoordCollectionRunning}
                />{' '}
                네이버 클라우드 지도 API 재수집
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="coord-collection-mode"
                  value="kakao_geocode"
                  checked={coordCollectionMode === 'kakao_geocode'}
                  onChange={() => setCoordCollectionMode('kakao_geocode')}
                  disabled={isCoordCollectionRunning}
                />{' '}
                카카오 지도 API 재수집
              </label>
            </div>
            <button
              type="button"
              className="dashboard-primary-btn"
              disabled={!selectedSido || isCoordCollectionRunning || checkingVworldTargets}
              onClick={runSelectedCoordCollection}
            >
              {coordCollectionButtonLabel}
            </button>
            {isCoordCollectionRunning && (
              <button
                type="button"
                className="dashboard-secondary-btn"
                style={{ marginLeft: '10px' }}
                disabled={runProgressByTask[activeCoordTaskName]?.stage === 'cancelling'}
                onClick={() => cancelTask(activeCoordTaskName, activeCoordTaskLabel)}
              >
                {runProgressByTask[activeCoordTaskName]?.stage === 'cancelling' ? '중단 요청 중...' : '작업 중단'}
              </button>
            )}
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px' }}>
            <h4 style={{ marginTop: 0 }}>2. 한전 주소-선로용량 수집</h4>
            {runningKepcoLine && (
              <div style={{ marginBottom: '12px', color: '#93c5fd', fontSize: '14px', fontWeight: 700 }}>
                진행 중
                {kepcoLineRunMeta?.run_label ? ` · ${kepcoLineRunMeta.run_label}` : ''}
                {kepcoLineRunMeta?.started_by ? ` · ${kepcoLineRunMeta.started_by}` : ''}
                {kepcoLineRunMeta?.started_at ? ` · ${formatDateTime(kepcoLineRunMeta.started_at)}` : ''}
              </div>
            )}
            <p style={{ marginTop: 0, marginBottom: '14px', color: '#6b7280', fontSize: '14px' }}>
              선택된 시도의 기존 DB 주소를 기준으로 배전선로, 주변압기, 변전소 용량 정보를 업데이트합니다.
              주소가 부족하면 1번 한전 주소-좌표 수집에서 먼저 `한전 주소 재수집`을 실행해주세요.
            </p>
            {linePreview && kepcoLineUpdateMode === 'capacity_only' && (
              <div className="grid-upload-info" style={{ marginTop: 0, marginBottom: '14px' }}>
                용량 미리보기: 전체 주소 {Number(linePreview.total_address_count || 0).toLocaleString()}건 / 이미 용량 있음 {Number(linePreview.address_with_capacity_count || 0).toLocaleString()}건 / 기존 행이지만 용량 비어 있음 {Number(linePreview.address_without_capacity_count || 0).toLocaleString()}건 / 선로 데이터 자체 없음 {Number(linePreview.address_missing_line_count || 0).toLocaleString()}건 / 이번 모드에서 갱신 대상 {Number(linePreview.updatable_address_count || 0).toLocaleString()}건
              </div>
            )}
            <div style={{ marginBottom: '14px', fontSize: '14px', color: '#334155' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>
                업데이트 모드
              </label>
              <label style={{ display: 'block', marginBottom: '6px' }}>
                <input
                  type="radio"
                  name="kepco-line-update-mode"
                  value="capacity_only"
                  checked={kepcoLineUpdateMode === 'capacity_only'}
                  onChange={() => setKepcoLineUpdateMode('capacity_only')}
                  disabled={runningKepcoLine}
                />{' '}
                주소는 유지하고 용량 관련 데이터만 업데이트
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="kepco-line-update-mode"
                  value="full"
                  checked={kepcoLineUpdateMode === 'full'}
                  onChange={() => setKepcoLineUpdateMode('full')}
                  disabled={runningKepcoLine}
                />{' '}
                기존 입력된 내용을 포함해 전부 업데이트
              </label>
            </div>
            <div style={{ marginBottom: '14px', fontSize: '14px', color: '#334155' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700 }}>
                저장 방식
              </label>
              <label style={{ display: 'block', marginBottom: '6px' }}>
                <input
                  type="radio"
                  name="kepco-line-output-mode"
                  value="db"
                  checked={kepcoLineOutputMode === 'db'}
                  onChange={() => setKepcoLineOutputMode('db')}
                  disabled={runningKepcoLine}
                />{' '}
                서버 DB에 바로 반영
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  name="kepco-line-output-mode"
                  value="csv"
                  checked={kepcoLineOutputMode === 'csv'}
                  onChange={() => setKepcoLineOutputMode('csv')}
                  disabled={runningKepcoLine}
                />{' '}
                CSV 파일로만 저장해서 다른 PC/서버에서 나중에 반영
              </label>
              {kepcoLineOutputMode === 'csv' && (
                <div className="grid-upload-info" style={{ marginTop: '10px', marginBottom: 0 }}>
                  CSV는 백엔드 `system_db_files/export/address_line_capacity` 폴더에 저장됩니다. 서버 DB 반영 시 해당 CSV를 `system_db_files/inbox/address_line_capacity`로 옮긴 뒤 주소-선로용량 폴더 업데이트를 실행하면 됩니다.
                </div>
              )}
            </div>
            <button
              type="button"
              className="dashboard-primary-btn"
              disabled={!selectedSido || runningKepcoLine || creatingKepcoAwsPlan}
              onClick={collectLineFromKepco}
            >
              {runningKepcoLine ? '실행 중...' : kepcoLineOutputMode === 'csv' ? '한전 선로용량 CSV 저장 실행' : '한전 주소-선로용량 수집 실행'}
            </button>
            <button
              type="button"
              className="dashboard-secondary-btn"
              style={{ marginLeft: '10px' }}
              disabled={!selectedSido || runningKepcoLine || creatingKepcoAwsPlan || launchingKepcoAwsEc2}
              onClick={createKepcoAwsPlan}
            >
              {creatingKepcoAwsPlan ? 'AWS 계획 생성 중...' : 'AWS 도별 PC 작업 계획 생성'}
            </button>
            {runningKepcoLine && (
              <button
                type="button"
                className="dashboard-secondary-btn"
                style={{ marginLeft: '10px' }}
                disabled={runProgressByTask.kepco_address_line_capacity?.stage === 'cancelling'}
                onClick={() => cancelTask('kepco_address_line_capacity', '한전 선로용량 수집')}
              >
                {runProgressByTask.kepco_address_line_capacity?.stage === 'cancelling' ? '중단 요청 중...' : '작업 중단'}
              </button>
            )}
            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #e5e7eb', display: 'grid', gap: '12px' }}>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                AWS 실행은 실제 EC2 과금이 시작될 수 있으므로 먼저 설정 점검을 실행한 뒤 실제 생성을 진행하세요.
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                <label htmlFor="kepco-aws-plan-file" style={{ fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                  AWS 작업 계획 파일
                </label>
                <input
                  id="kepco-aws-plan-file"
                  type="text"
                  value={kepcoAwsPlanFile}
                  onChange={(e) => setKepcoAwsPlanFile(e.target.value)}
                  placeholder="src/backend/system_db_files/export/aws_kepco_line_capacity/...json"
                  disabled={launchingKepcoAwsEc2}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                    background: '#fff',
                    color: '#0f172a',
                  }}
                />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#334155' }}>
                <input
                  type="checkbox"
                  checked={kepcoAwsDryRun}
                  onChange={(e) => setKepcoAwsDryRun(e.target.checked)}
                  disabled={launchingKepcoAwsEc2}
                />
                기본 실행은 AWS 설정 점검(Dry Run)으로 시작
              </label>
              <div>
                <button
                  type="button"
                  className="dashboard-secondary-btn"
                  disabled={!kepcoAwsPlanFile || creatingKepcoAwsPlan || launchingKepcoAwsEc2}
                  onClick={() => launchKepcoAwsEc2(true)}
                >
                  {launchingKepcoAwsEc2 && kepcoAwsLaunchMode === 'dry_run' ? 'AWS 설정 점검 중...' : 'AWS EC2 설정 점검'}
                </button>
                <button
                  type="button"
                  className="dashboard-primary-btn"
                  style={{ marginLeft: '10px' }}
                  disabled={!kepcoAwsPlanFile || creatingKepcoAwsPlan || launchingKepcoAwsEc2 || kepcoAwsDryRun}
                  onClick={() => launchKepcoAwsEc2(false)}
                >
                  {launchingKepcoAwsEc2 && kepcoAwsLaunchMode === 'launch' ? 'AWS EC2 생성 중...' : 'AWS EC2 생성 실행'}
                </button>
              </div>
              {kepcoAwsJobs.length > 0 && (
                <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '12px', display: 'grid', gap: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#334155' }}>
                    생성된 AWS 인스턴스 로그 보기
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {kepcoAwsJobs.map((job) => {
                      const logInstanceId = job.log_instance_id || job.instance_id || '';
                      const isActive = selectedKepcoAwsInstanceId === logInstanceId;
                      return (
                        <button
                          key={`${job.instance_name}-${logInstanceId || job.worker_index}`}
                          type="button"
                          className={isActive ? 'dashboard-primary-btn' : 'dashboard-secondary-btn'}
                          style={{ minWidth: 'unset' }}
                          disabled={!logInstanceId || loadingKepcoAwsInstanceLogs}
                          onClick={() => fetchKepcoAwsInstanceLogs(logInstanceId)}
                        >
                          {job.instance_name}
                        </button>
                      );
                    })}
                  </div>
                  {selectedKepcoAwsInstanceId && (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>
                        인스턴스 ID: {selectedKepcoAwsInstanceMeta?.instance_id || selectedKepcoAwsInstanceId}
                        {selectedKepcoAwsInstanceMeta?.status ? ` / 상태: ${selectedKepcoAwsInstanceMeta.status}` : ''}
                        {selectedKepcoAwsInstanceMeta?.updated_at ? ` / 마지막 갱신: ${formatDateTime(selectedKepcoAwsInstanceMeta.updated_at)}` : ''}
                      </div>
                      <textarea
                        readOnly
                        value={
                          loadingKepcoAwsInstanceLogs
                            ? 'AWS 인스턴스 로그를 불러오는 중입니다...'
                            : selectedKepcoAwsInstanceLogLines.length
                              ? selectedKepcoAwsInstanceLogLines.map((entry) => `[${entry.timestamp || '-'}] ${entry.level || 'INFO'} ${entry.event_type || 'log'} ${entry.message || ''}`).join('\n')
                              : '아직 서버로 수집된 로그가 없습니다. 인스턴스 부팅 후 1~3분 정도 지난 뒤 다시 확인해주세요.'
                        }
                        style={{
                          width: '100%',
                          minHeight: '220px',
                          padding: '12px',
                          borderRadius: '12px',
                          border: '1px solid #cbd5e1',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: '12px',
                          lineHeight: 1.5,
                          resize: 'vertical',
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: '14px', padding: '16px' }}>
            <h4 style={{ marginTop: 0 }}>3. 주소-토지정보 수집</h4>
            {runningLandInfo && (
              <div style={{ marginBottom: '12px', color: '#93c5fd', fontSize: '14px', fontWeight: 700 }}>
                진행 중
                {landInfoRunMeta?.run_label ? ` · ${landInfoRunMeta.run_label}` : ''}
                {landInfoRunMeta?.started_by ? ` · ${landInfoRunMeta.started_by}` : ''}
                {landInfoRunMeta?.started_at ? ` · ${formatDateTime(landInfoRunMeta.started_at)}` : ''}
              </div>
            )}
            <p style={{ marginTop: 0, marginBottom: '14px', color: '#6b7280', fontSize: '14px' }}>
              저장된 주소-좌표 데이터를 기준으로 토지이용정보서비스 API에서 용도지역과 지목정보를 받아 `주소-토지정보` 테이블에 적재합니다.
            </p>
            {landInfoPreview && (
              <div className="grid-upload-info" style={{ marginTop: 0, marginBottom: '14px' }}>
                토지정보 미리보기: 전체 주소 {Number(landInfoPreview.total_address_count || 0).toLocaleString()}건 / 기존 토지정보 {Number(landInfoPreview.existing_land_info_count || 0).toLocaleString()}건 / 이번 갱신 대상 {Number(landInfoPreview.target_count || 0).toLocaleString()}건
              </div>
            )}
            <button
              type="button"
              className="dashboard-primary-btn"
              disabled={!selectedSido || runningLandInfo}
              onClick={collectLandInfo}
            >
              {runningLandInfo ? '실행 중...' : '토지이용정보 수집 실행'}
            </button>
            {runningLandInfo && (
              <button
                type="button"
                className="dashboard-secondary-btn"
                style={{ marginLeft: '10px' }}
                disabled={runProgressByTask.land_use_address_info?.stage === 'cancelling'}
                onClick={() => cancelTask('land_use_address_info', '토지이용정보 수집')}
              >
                {runProgressByTask.land_use_address_info?.stage === 'cancelling' ? '중단 요청 중...' : '작업 중단'}
              </button>
            )}
          </div>
        </div>

        {resultMessage && (
          <div className="grid-upload-success" style={{ marginTop: '16px' }}>
            {resultMessage}
          </div>
        )}

        {errorMessage && (
          <div className="grid-upload-error" style={{ marginTop: '16px' }}>
            {errorMessage}
          </div>
        )}

        <div className="system-db-runlog-grid" style={{ marginTop: '18px' }}>
          {RUN_PROGRESS_CONFIG.map(({ taskName, title }) => {
            const progress = runProgressByTask[taskName] || {};
            const isRunning = taskName === 'kepco_address_coordinates'
              ? runningKepcoCoord
              : taskName === 'vworld_address_coordinates'
                ? runningVworldCoord
                : taskName === 'naver_address_coordinates'
                  ? runningNaverCoord
                  : taskName === 'kakao_address_coordinates'
                    ? runningKakaoCoord
                    : taskName === 'land_use_address_info'
                      ? runningLandInfo
                      : runningKepcoLine;

            return (
              <div key={taskName} className="system-db-runlog-card">
                <div className="system-db-runlog-header">
                  <strong>{title}</strong>
                  <span className={`system-db-runlog-badge ${isRunning ? 'running' : ''}`}>
                    {progress?.stage === 'cancelling'
                      ? 'STOPPING'
                      : isRunning
                        ? 'LIVE'
                        : progress?.stage === 'completed'
                          ? 'DONE'
                          : progress?.stage === 'failed'
                            ? 'FAIL'
                            : progress?.stage === 'cancelled'
                              ? 'STOPPED'
                              : 'READY'}
                  </span>
                </div>
                <div className="system-db-progress-summary">
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">상태</span>
                    <span className="system-db-progress-value">{progress?.message || '대기 중'}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">현재 시도</span>
                    <span className="system-db-progress-value">{progress?.current_sido || '-'}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">총 요청</span>
                    <span className="system-db-progress-value">{Number(progress?.total_requests || 0).toLocaleString()}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">완료 요청</span>
                    <span className="system-db-progress-value">{Number(progress?.completed_requests || 0).toLocaleString()}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">성공</span>
                    <span className="system-db-progress-value">{Number(progress?.success_requests || 0).toLocaleString()}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">응답 없음</span>
                    <span className="system-db-progress-value">{Number(progress?.empty_requests || 0).toLocaleString()}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">오류</span>
                    <span className="system-db-progress-value">{Number(progress?.error_requests || 0).toLocaleString()}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">배치 진행</span>
                    <span className="system-db-progress-value">
                      {Number(progress?.completed_batches || 0).toLocaleString()} / {Number(progress?.total_batches || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="system-db-progress-item system-db-progress-item-wide">
                    <span className="system-db-progress-label">현재 요청 자료</span>
                    <span className="system-db-progress-value">{progress?.current_request || '-'}</span>
                  </div>
                  <div className="system-db-progress-item">
                    <span className="system-db-progress-label">마지막 갱신</span>
                    <span className="system-db-progress-value">{formatDateTime(progress?.updated_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <SystemDbBrowserView />
    </div>
  );
}
